package com.evoapps.evoshift.shell

import androidx.compose.runtime.staticCompositionLocalOf
import com.evoapps.evoshift.navigation.AppShortcut
import com.evoapps.evoshift.navigation.AppTab
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.datetime.YearMonth

/**
 * What a desktop menu bar asks the app to do (SH-97). The keyboard shortcuts are the app's one
 * registry ([AppShortcut], SH-98): a menu item with a shortcut sends [Shortcut], handled exactly
 * as the key is (the screen that offers it, else the shell). The other items have no key.
 * [com.evoapps.evoshift.AdaptiveAppLayout] carries them out with the navigation the bars use, so
 * a command can never open what the person may not see.
 */
sealed interface AppCommand {
    /** The same as pressing the shortcut's key (⌘ on a Mac, Ctrl elsewhere). */
    data class Shortcut(val shortcut: AppShortcut) : AppCommand

    data object ThisMonth : AppCommand

    /** The workplace chooser. */
    data object SwitchWorkplace : AppCommand
    data class SelectWorkplace(val workplaceId: String) : AppCommand
    data object OpenNotifications : AppCommand

    /**
     * A click on the system notification of in-app notification [id] (SH-141): opens what tapping
     * it in the list opens and marks it read; the list when it is no longer loaded.
     */
    data class OpenNotification(val id: String) : AppCommand
    data object OpenProfile : AppCommand
    data object Back : AppCommand
}

/** What the shell offers right now: the menu bar's names and enabled states follow it. */
data class ShellState(
    val signedIn: Boolean = false,
    val tabs: List<AppTab> = emptyList(),
    val selectedTab: AppTab? = null,
    /** The shortcuts that do something now (the same set the shortcut helper lists). */
    val shortcuts: Set<AppShortcut> = emptySet(),
    val canGoBack: Boolean = false,
    /** Workplace id to name, as in the workplace chooser. */
    val workplaces: Map<String, String> = emptyMap(),
    val workplaceId: String? = null,
    val month: YearMonth? = null,
    val unreadNotifications: Int = 0
)

/**
 * A new in-app notification as the system shows it (SH-141), already in the reader's language and
 * filtered by their notification settings; [id] comes back in [AppCommand.OpenNotification].
 */
data class SystemNotification(val id: String, val title: String, val body: String)

/**
 * The bridge between a platform's own chrome (the macOS menu bar, SH-97) and the app: the chrome
 * [send]s [AppCommand]s and reads [state]. Platforms without such chrome never provide one
 * ([LocalAppShell] is null), and the app behaves exactly as before.
 */
class AppShell {
    private val _commands = Channel<AppCommand>(Channel.BUFFERED)

    /** The commands, each delivered once, in order. */
    val commands: Flow<AppCommand> = _commands.receiveAsFlow()

    private val _state = MutableStateFlow(ShellState())
    val state: StateFlow<ShellState> = _state.asStateFlow()

    fun send(command: AppCommand) {
        _commands.trySend(command)
    }

    /** Called by the app shell whenever what it offers changes. */
    fun publish(state: ShellState) {
        _state.value = state
    }

    private val _notifications = Channel<SystemNotification>(Channel.BUFFERED)

    /** New in-app notifications for the system's notification centre (SH-141), each once. */
    val notifications: Flow<SystemNotification> = _notifications.receiveAsFlow()

    /** Called by the app for each notification that arrived while it runs ([ShellNotifications]). */
    fun notify(notification: SystemNotification) {
        _notifications.trySend(notification)
    }
}

/** The platform chrome's [AppShell] (the desktop app's), or null on phones, tablets and the web. */
val LocalAppShell = staticCompositionLocalOf<AppShell?> { null }
